import {
  Autocomplete,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { School } from "../../../../interfaces";
import axios from "../../../../services/axios";
import { ReactComponent as SchoolIcon } from "../../../../assets/Icon-SchoolBuilding.svg";
import { useSearch } from "../../../../hooks/useSearch";
import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory, useLocation } from "react-router-dom";
import { generateGtmEvent } from "../../../../Helpers";

interface SchoolSearchProps {
  selected: School | null;
  onSelect: (school: School | null) => void;

  isDisabled?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: (props: SchoolSearchProps) => ({
    "& fieldset": {
      border: "2px solid #DEDEDE",
      borderRadius: "0.5rem",
    },
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& input": {
      // color: props.selected ? theme.palette.primary.dark : "#484848",
      // fontWeight: props.selected ? "500" : "unset",
      color: theme.palette.text.secondary,
      "&::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },
    "& svg": {
      // color: props.selected ? theme.palette.primary.main : "unset",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: "1rem",
      paddingRight: "1rem !important",
      "&:hover fieldset": {
        border: `2px solid ${theme.palette.primary.main}`,
      },
    },
  }),
  schoolIcon: {
    transform: "translateY(-2px)",
    color: theme.palette.text.secondary,
  },
  closeButton: {
    color: theme.palette.text.secondary,
    "&:hover": {
      color: "#E74973",
    },
  },
  dropdownIcon: {
    color: theme.palette.text.secondary,
    marginLeft: "0.5rem",
  },
}));

export const SchoolSearch = (props: SchoolSearchProps) => {
  const classes = useStyles(props);
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const { isDisabled = false, onSelect } = props;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const [initialized, setInitialized] = useState(false);

  const { result, loading, onSearchChange, searchText } = useSearch({
    queryFn: async (state) => {
      const res = await axios.get<School[]>("/schools", {
        signal: state.signal,
        params: { filter: state.searchText },
      });
      return res.data;
    },
    defaultValue: [] as School[],
  });
  useEffect(() => {
    onSearchChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const location = useLocation();
  let queryParams = new URLSearchParams(location.search);
  const schoolId = queryParams.get("schoolId");
  const [inputFocused, setInputFocused] = useState(false);
  const [inputHovered, setInputHovered] = useState(false);

  useEffect(() => {
    if (schoolId && result.length > 0 && !initialized) {
      const school = result.find((school) => school.id === Number(schoolId));
      onSelectRef.current(school ?? null);
      if (school) {
        setInitialized(true);
      }
    }
  }, [schoolId, result, initialized]);

  return (
    <Autocomplete
      onMouseEnter={() => setInputHovered(true)}
      onMouseLeave={() => setInputHovered(false)}
      disabled={isDisabled}
      disablePortal
      blurOnSelect
      id="school-search"
      value={props.selected ?? null}
      onChange={(e, school) => {
        history.push({
          search: school ? `?schoolId=${school.id}` : "",
        });
        generateGtmEvent({
          event: "select",
          action: "click",
          name: "onclick",
          type: "select a school",
          region: "main content",
          section: "all classes",
          text: `${school?.name}`,
        });
        onSelect(school);
      }}
      open={open}
      onInputChange={(e, value, reason) => {
        onSearchChange(value);
        // Prevent opening on selection
        // and close when text is empty
        if (reason !== "reset") {
          setOpen(!!value.length);
        }
      }}
      onOpen={() => {
        // We want to keep the menu closed
        // until the user types in order to
        // prevent showing the "no options"
        // message when there is empty text.
        if (searchText?.length) {
          setOpen(true);
        }
      }}
      onClose={() => setOpen(false)}
      options={result}
      getOptionLabel={(school) => school.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      style={{ width: 300 }}
      className={classes.root}
      filterOptions={(v) => v}
      renderInput={(params) => (
        <TextField
          {...params}
          label={inputFocused || props.selected ? "School" : ""}
          placeholder="Select a school"
          onFocus={(e) => {
            setInputFocused(true);
          }}
          onChange={(e) => {
            generateGtmEvent({
              event: "form",
              action: "click",
              name: "onclick",
              type: "blur",
              region: "main content",
              section: "all classes ^ school",
              text: `${e.target.value}`,
            });
          }}
          onBlur={(e) => {
            setInputFocused(false);
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" className={classes.schoolIcon}>
                <SchoolIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end" className={classes.schoolIcon}>
                {inputHovered && props.selected ? (
                  <Tooltip
                    placement="bottom"
                    title="Clear current search"
                    arrow
                    role="tooltip"
                    describeChild>
                    <IconButton
                      disabled={isDisabled}
                      sx={{ width: "1.5rem", height: "1.5rem" }}
                      onClick={() => {
                        onSelect(null);
                        setOpen(false);
                      }}
                      aria-label="clear current search"
                      className={classes.closeButton}>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {open ? (
                  <Tooltip
                    placement="bottom"
                    title="Close list"
                    arrow
                    role="tooltip"
                    describeChild>
                    <IconButton
                      sx={{ width: "2rem", height: "2rem" }}
                      disabled={isDisabled}
                      onClick={() => {
                        setOpen(false);
                      }}
                      aria-label="close list"
                      className={classes.dropdownIcon}>
                      <i className="fa-regular fa-chevron-up fa-1x"></i>
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    placement="bottom"
                    title="Open list"
                    arrow
                    role="tooltip"
                    describeChild>
                    <IconButton
                      sx={{ width: "2rem", height: "2rem" }}
                      disabled={isDisabled}
                      onClick={() => {
                        onSearchChange("");
                        setOpen(true);
                      }}
                      aria-label="Open list"
                      className={classes.dropdownIcon}>
                      <i className="fa-regular fa-chevron-down fa-1x"></i>
                    </IconButton>
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          }}
        />
      )}
      popupIcon={null}
      clearIcon={null}
    />
  );
};
